/**
 * ========================================
 * TOAST CONTAINER
 * ========================================
 * Renders all toast notifications at the top right
 */

import React from 'react';
import { useToast } from '@/hooks/useToast';

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  const getToastStyle = (type: string) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-900',
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          svg: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-900',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          svg: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          svg: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          svg: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
    }
  };

  return (
    <div className="fixed top-0 left-1/2 transform -translate-x-1/2 z-[9999] w-full max-w-lg pointer-events-none p-4">
      <div className="space-y-3">
        {toasts.map((toast) => {
          const style = getToastStyle(toast.type);
          return (
            <div
              key={toast.id}
              className={`${style.bg} ${style.border} ${style.text} border px-6 py-4 rounded-2xl shadow-2xl flex items-start gap-4 animate-in fade-in slide-in-from-top-6 duration-300 pointer-events-auto backdrop-blur-sm`}
            >
              <div className={`${style.iconBg} ${style.iconColor} rounded-full p-2.5 flex-shrink-0 mt-0.5`}>
                {style.svg}
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <p className="text-sm font-semibold leading-tight">{toast.message}</p>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className={`${style.iconColor} opacity-60 hover:opacity-100 transition-opacity flex-shrink-0`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
